import {
  Body,
  Divider,
  FormProvider,
  Input,
  Link,
  Loader,
  Stack,
  useForm,
  useWindowProperties,
} from '@qred/components-library';
import React, { useContext, useEffect } from 'react';
import { FieldPath } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { ValidationContext } from '~/components/hoc/withValidation';
import { FormErrorBanner } from '~/components/shared/FormErrorBanner/FormErrorBanner';
import StepHeader from '~/components/shared/Stepper/components/StepHeader';
import { phoneNumberCountryCodes, policyTermsUrl } from '~/constants/markets';
import { ClientType, FlowTypes } from '~/enums';
import { areSameCompanies, getClientIdByOrgNumber } from '~/helpers/utils';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import {
  resetCompanyInForm,
  setCompanyIsNew,
  updateApplicantData,
  updateForm,
} from '~/store/slices/loanApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import {
  StandardStepContent,
  StandardStepContentBody,
  StandardStepContentFooter,
} from '~/styles/BaseStyledComponents';
import { CompanySearch } from '../StartApplication/components/CompanySearch';
import { CompanySelect } from '../StartApplication/components/CompanySelect';
import { StartApplicationFooter } from '../StartApplication/components/StartApplicationFooter';
import {
  StartApplicationNewForm,
  createStartApplicationNewSchema,
} from './StartApplicationNew.schema';

/**
 * Step A of the 3-step basic flow (behind the
 * AB_Web_LoanApplication_ContactDetailsEarlier_MoreRecoverableLeads_NL flag,
 * currently tested in NL only): company identification + contact details.
 * Moving email/phone onto the very first step means they are captured in
 * the application-create POST, so a lead is recoverable by Marketing even
 * if the applicant drops off before finishing the rest of the basic flow
 * (OB-5217).
 */
export const StartApplicationNew = () => {
  const {
    form: {
      organizationNumber,
      companyType,
      companyName,
      applicant: { phone, email },
    },
    overview: { clients },
    apiStatus,
    companyIsNew: initialCompanyIsNew,
    flowType,
  } = useSelector((state: RootState) => state.loanApplication);

  const { market, language } = useSelector((state: RootState) => state.intl);

  const t = useTranslate();
  const validationContext = useContext(ValidationContext);
  const dispatch = useDispatch();
  const { isMobile } = useWindowProperties();

  const formMethods = useForm<StartApplicationNewForm>({
    schema: createStartApplicationNewSchema(market),
    mode: 'onTouched',
    defaultValues: {
      companyIsNew: flowType === FlowTypes.Manual ? true : initialCompanyIsNew,
      clientId: organizationNumber
        ? getClientIdByOrgNumber(organizationNumber, clients)
        : undefined,
      companyDetails:
        organizationNumber &&
        !getClientIdByOrgNumber(organizationNumber, clients)
          ? {
              companyName,
              companyType,
              companyNumber: organizationNumber || undefined,
            }
          : undefined,
      phone: phone || phoneNumberCountryCodes[market],
      email: email || '',
    },
  });

  const {
    watch,
    register,
    setValue,
    formState: { errors, isValid },
  } = formMethods;

  const clientId = watch('clientId');
  const companyIsNew = watch('companyIsNew');
  const companyDetails = watch('companyDetails');
  const phoneValue = watch('phone');
  const emailValue = watch('email');

  const handleCompanySelect = (selectedClientId: string) => {
    const newCompanySelected = selectedClientId === 'other';
    dispatch(resetCompanyInForm());

    if (newCompanySelected) {
      formMethods.setValue('clientId', undefined);
      formMethods.setValue('companyIsNew', true, { shouldValidate: true });
    } else {
      formMethods.setValue('clientId', selectedClientId);
      formMethods.setValue('companyIsNew', false, { shouldValidate: true });
      formMethods.unregister('companyDetails');
    }
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const trimmedValue = value.trim();
    setValue(name as FieldPath<StartApplicationNewForm>, trimmedValue);
    dispatch(
      pushToGtmLoanAction({
        actionName: `${name}_change`,
      })
    );
  };

  useEffect(() => {
    if (!isValid) {
      validationContext.manuallyInvalidateForm(true);
    }

    return () => {
      validationContext.manuallyInvalidateForm(false);
    };
  }, [isValid]);

  // The store falls back to the company search when the user's companies or
  // clients can't be loaded, which can happen after this step has mounted.
  useEffect(() => {
    if (initialCompanyIsNew) {
      formMethods.setValue('companyIsNew', true, { shouldValidate: true });
    }
  }, [initialCompanyIsNew]);

  useEffect(() => {
    if (apiStatus.clients === ApiStatus.Completed) {
      const orgNumberExistsInListOfClients = clients.some((c) =>
        areSameCompanies(organizationNumber, c.company_no)
      );
      const _companyIsNew =
        clients.length === 0 ||
        (Boolean(organizationNumber) && !orgNumberExistsInListOfClients);
      formMethods.setValue('companyIsNew', _companyIsNew);
    }
  }, [apiStatus.clients, clients]);

  useEffect(() => {
    if (isValid) {
      dispatch(setCompanyIsNew(companyIsNew));

      if (clientId) {
        const client = clients.find(
          (_client) => _client.client_id === Number(clientId)
        );

        if (client) {
          dispatch(
            updateForm({
              organizationNumber: client.company_no ?? '',
              companyType: client.company_type ?? '',
              clientType: client.active ? ClientType.RLC : ClientType.NLC,
              companyName: client.company_name,
            })
          );
        }
      } else if (companyDetails) {
        dispatch(
          updateForm({
            organizationNumber: companyDetails.companyNumber,
            companyType: companyDetails.companyType,
            companyName: companyDetails.companyName,
          })
        );
      }

      dispatch(
        updateApplicantData({
          phone: phoneValue,
          email: emailValue,
        })
      );
    }
  }, [isValid, clientId, companyDetails, phoneValue, emailValue, dispatch]);

  return (
    <StandardStepContent>
      <StandardStepContentBody>
        <StepHeader
          title="Onboarding.StartApplicationTitle"
          description="Onboarding.StartApplicationDescription"
        />
        <FormProvider {...formMethods}>
          <Input.Group orientation="vertical">
            {apiStatus.clients === ApiStatus.Started ? (
              <Loader />
            ) : (
              <>
                {clients.length > 0 && (
                  <CompanySelect
                    clients={clients}
                    companyIsNew={companyIsNew}
                    handleCompanySelect={handleCompanySelect}
                  />
                )}
                {companyIsNew && <CompanySearch />}
              </>
            )}
            <Input
              {...register('email', { onBlur })}
              label={t('Email')}
              error={errors?.email?.message && t(errors?.email?.message)}
              id="email"
            />
            <Input
              {...register('phone', { onBlur })}
              label={t('MobilePhone')}
              error={errors?.phone?.message && t(errors?.phone?.message)}
              id="phone"
            />
          </Input.Group>
        </FormProvider>
        <FormErrorBanner errors={errors} />
        <Stack mt={isMobile ? undefined : 'auto'}>
          {!isMobile && <Divider color="Border/Minimal" />}
          <Body color="Text/Secondary" size="sm">
            {t('Onboarding.MarketingConsent', {
              next: t('Onboarding.Next'),
              email: (
                <a href="mailto:privacy@qred.com">
                  <Link size="sm">privacy@qred.com</Link>
                </a>
              ),
              marketingPrivacyNoticeLinkText: (
                <a
                  href={policyTermsUrl[market][language]}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Link size="sm">
                    {t('Onboarding.MarketingPrivacyNoticeLinkText')}
                  </Link>
                </a>
              ),
            })}
          </Body>
        </Stack>
      </StandardStepContentBody>
      <StandardStepContentFooter>
        <StartApplicationFooter />
      </StandardStepContentFooter>
    </StandardStepContent>
  );
};
